/**
 * Throttled resize handling for cross-browser consistency.
 *
 * Chrome (and other Chromium-based browsers) fire the window "resize" event
 * much more frequently during a resize than Edge/Firefox, which can cause
 * layout thrashing, canvas/WebGL thrashing, and visual glitches. This hook
 * and utility throttle resize callbacks to at most once per animation frame
 * (or at a fixed interval), so behavior is smooth and consistent across
 * browsers.
 *
 * Chrome also triggers resize when the scrollbar appears/disappears (~15–17px
 * width change), which can cause a feedback loop: update size → re-render →
 * scrollbar toggles → resize → update size → … Use the threshold option or
 * resizeOnlyWhenChangedBy() to only run when dimensions change meaningfully.
 */

import { useEffect, useRef } from 'react';

/** Default throttle: run at most once per animation frame (~60fps cap). */
const DEFAULT_THROTTLE_MS = 0; // 0 = use requestAnimationFrame

/** Min change in px before we treat width/height as changed (avoids scrollbar oscillation). */
export const RESIZE_CHANGE_THRESHOLD_PX = 24;

/**
 * Wraps a resize callback so it only runs when the size getter returns dimensions
 * that have changed by more than the threshold. Use to avoid scrollbar-induced
 * feedback loops (slightly bigger/smaller every second in Chrome).
 *
 * @param getSize - Function that returns current { width, height } (e.g. from window or container)
 * @param onResize - Callback to run when size changed meaningfully
 * @param thresholdPx - Min change in either dimension to trigger (default 24)
 * @returns A function to call on resize; only calls onResize when change exceeds threshold
 */
export function resizeOnlyWhenChangedBy(
  getSize: () => { width: number; height: number },
  onResize: () => void,
  thresholdPx: number = RESIZE_CHANGE_THRESHOLD_PX
): () => void {
  let lastWidth = 0;
  let lastHeight = 0;
  let initialized = false;

  return function checkAndRun() {
    const { width, height } = getSize();
    if (
      !initialized ||
      Math.abs(width - lastWidth) > thresholdPx ||
      Math.abs(height - lastHeight) > thresholdPx
    ) {
      initialized = true;
      lastWidth = width;
      lastHeight = height;
      onResize();
    }
  };
}

/**
 * Creates a throttled version of a callback that runs at most once per
 * requestAnimationFrame (or every throttleMs if > 0). Used for resize handlers
 * to avoid Chrome's high resize event rate causing glitches.
 *
 * @param fn - Callback to run (e.g. resize logic)
 * @param throttleMs - If 0, use rAF; if > 0, use time-based throttle in ms
 * @returns Throttled function; call from window "resize" listener
 */
export function throttleResizeCallback(
  fn: () => void,
  throttleMs: number = DEFAULT_THROTTLE_MS
): () => void {
  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastRun = 0;
  let active = true;

  const run = () => {
    if (!active) return;
    rafId = null;
    timeoutId = null;
    lastRun = Date.now();
    fn();
  };

  const cancel = () => {
    active = false;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  if (throttleMs > 0) {
    return Object.assign(function throttled() {
      if (timeoutId != null) return;
      const elapsed = Date.now() - lastRun;
      if (elapsed >= throttleMs) {
        run();
      } else {
        timeoutId = setTimeout(run, throttleMs - elapsed);
      }
    }, { cancel });
  }

  return Object.assign(function throttled() {
    if (rafId != null) return;
    active = true; // Reset active flag in case function was cancelled and is being reused
    rafId = requestAnimationFrame(run);
  }, { cancel });
}

/**
 * Subscribes to window "resize" with a throttled callback. Cleans up on
 * unmount. Use for canvas, WebGL, or layout logic that should not run
 * on every resize event (avoids Chrome resize glitches).
 *
 * @param onResize - Callback to run when window is resized (throttled)
 * @param options - { throttleMs: 0 } = rAF throttle; > 0 = time-based throttle
 */
export function useThrottledResize(
  onResize: () => void,
  options: { throttleMs?: number } = {}
): void {
  const { throttleMs = DEFAULT_THROTTLE_MS } = options;
  const fnRef = useRef(onResize);
  fnRef.current = onResize;

  useEffect(() => {
    const throttled = throttleResizeCallback(() => {
      fnRef.current();
    }, throttleMs);

    window.addEventListener('resize', throttled);
    return () => {
      if ('cancel' in throttled && typeof throttled.cancel === 'function') {
        throttled.cancel();
      }
      window.removeEventListener('resize', throttled);
    };
  }, [throttleMs, onResize]);
}

/**
 * Hook that runs a callback once on mount and then on every throttled resize.
 * Convenience when you need both initial run and resize handling.
 *
 * @param onResize - Callback (e.g. resize canvas / update dimensions)
 * @param options - { throttleMs: 0 } = rAF; > 0 = time-based
 */
export function useResizeEffect(
  onResize: () => void,
  options: { throttleMs?: number } = {}
): void {
  const { throttleMs = DEFAULT_THROTTLE_MS } = options;
  const fnRef = useRef(onResize);
  fnRef.current = onResize;

  useEffect(() => {
    const throttled = throttleResizeCallback(() => fnRef.current(), throttleMs);

    fnRef.current(); // run once on mount
    window.addEventListener('resize', throttled);
    return () => {
      if ('cancel' in throttled && typeof throttled.cancel === 'function') {
        throttled.cancel();
      }
      window.removeEventListener('resize', throttled);
    };
  }, [throttleMs, onResize]);
}
